/**
 * Chart scales and tick generation.
 *
 * Pure maths, no React, no DOM — every chart in the kit shares these so axes are
 * consistent across the console and the whole lot is unit-testable.
 */

export interface LinearScale {
  (value: number): number
  domain: [number, number]
  range: [number, number]
  /** Screen position back to data value — needed for crosshairs. */
  invert(position: number): number
}

export function linearScale(domain: [number, number], range: [number, number]): LinearScale {
  const [d0, d1] = domain
  const [r0, r1] = range
  const span = d1 - d0
  const scale = ((value: number) => {
    if (span === 0) return (r0 + r1) / 2
    return r0 + ((value - d0) / span) * (r1 - r0)
  }) as LinearScale
  scale.domain = domain
  scale.range = range
  scale.invert = (position: number) => {
    const rSpan = r1 - r0
    if (rSpan === 0) return d0
    return d0 + ((position - r0) / rSpan) * span
  }
  return scale
}

/** Evenly spaced band positions — one slot per category. */
export interface BandScale {
  (index: number): number
  bandwidth: number
  step: number
  /** Centre of the band at `index`. */
  center(index: number): number
  /** Which band a screen position falls in, clamped to the data. */
  indexAt(position: number): number
}

export function bandScale(count: number, range: [number, number], padding = 0.2): BandScale {
  const [r0, r1] = range
  const width = r1 - r0
  const step = count > 0 ? width / count : width
  const bandwidth = Math.max(1, step * (1 - padding))
  const offset = (step - bandwidth) / 2

  const scale = ((index: number) => r0 + index * step + offset) as BandScale
  scale.bandwidth = bandwidth
  scale.step = step
  scale.center = (index: number) => r0 + index * step + step / 2
  scale.indexAt = (position: number) => {
    if (count === 0) return 0
    const raw = Math.floor((position - r0) / step)
    return Math.min(count - 1, Math.max(0, raw))
  }
  return scale
}

/**
 * Human-friendly tick values covering a domain — the 1/2/5/10 progression, so
 * an axis reads 0, 25k, 50k rather than 0, 23.7k, 47.4k.
 */
export function niceTicks(min: number, max: number, target = 5): number[] {
  if (!Number.isFinite(min) || !Number.isFinite(max)) return []
  if (min === max) return [min]
  const span = max - min
  const rough = span / Math.max(1, target)
  const magnitude = Math.pow(10, Math.floor(Math.log10(rough)))
  const normalised = rough / magnitude
  // Geometric-mean boundaries (sqrt(50), sqrt(10), sqrt(2)) pick the step whose
  // tick count lands closest to the target — the same rule d3 uses.
  const step =
    (normalised >= 7.071 ? 10 : normalised >= 3.162 ? 5 : normalised >= 1.414 ? 2 : 1) * magnitude

  const start = Math.ceil(min / step) * step
  const ticks: number[] = []
  // Guard against a pathological step producing an unbounded loop.
  for (let v = start, i = 0; v <= max + step * 1e-9 && i < 1000; v += step, i++) {
    ticks.push(Math.abs(v) < step * 1e-9 ? 0 : round(v, step))
  }
  return ticks
}

/** Round to the precision implied by the step, killing float dust like 0.30000000000000004. */
function round(value: number, step: number): number {
  const decimals = Math.max(0, -Math.floor(Math.log10(step)) + 1)
  return Number(value.toFixed(Math.min(10, decimals)))
}

/**
 * A domain padded outward to the next tick so the top of a series never touches
 * the top of the plot, and zero is always included on a value axis.
 */
export function niceDomain(
  values: number[],
  opts: { includeZero?: boolean; padding?: number; symmetric?: boolean } = {},
): [number, number] {
  const { includeZero = true, padding = 0.08, symmetric = false } = opts
  const finite = values.filter((v) => Number.isFinite(v))
  if (finite.length === 0) return [0, 1]

  let min = Math.min(...finite)
  let max = Math.max(...finite)
  if (includeZero) {
    min = Math.min(min, 0)
    max = Math.max(max, 0)
  }
  if (min === max) {
    // A flat series still needs a plot area with height.
    const bump = Math.abs(min) > 0 ? Math.abs(min) * 0.5 : 1
    min -= bump
    max += bump
  }

  const span = max - min
  min -= span * padding
  max += span * padding

  if (symmetric) {
    const extent = Math.max(Math.abs(min), Math.abs(max))
    return [-extent, extent]
  }
  if (includeZero) {
    min = Math.min(min, 0)
    max = Math.max(max, 0)
  }
  return [min, max]
}

/** Cumulative running total — the P&L curve is drawn from this. */
export function cumulative(values: number[]): number[] {
  let total = 0
  return values.map((v) => (total += Number.isFinite(v) ? v : 0))
}

/** Centred moving average, used for trend overlays on noisy daily series. */
export function movingAverage(values: number[], window: number): (number | null)[] {
  if (window <= 1) return values.slice()
  const half = Math.floor(window / 2)
  return values.map((_, i) => {
    const from = i - half
    const to = i + half
    if (from < 0 || to >= values.length) return null
    let sum = 0
    for (let j = from; j <= to; j++) sum += values[j] ?? 0
    return sum / (to - from + 1)
  })
}

/**
 * An SVG path through points, optionally smoothed with a monotone cubic so a
 * curve never overshoots its data — important on money charts, where an
 * invented dip below zero would be a lie.
 */
export function linePath(points: [number, number][], smooth = false): string {
  if (points.length === 0) return ''
  if (points.length === 1) {
    const [x, y] = points[0]!
    return `M ${x} ${y}`
  }
  if (!smooth) {
    return points.map(([x, y], i) => `${i === 0 ? 'M' : 'L'} ${fmt(x)} ${fmt(y)}`).join(' ')
  }

  const tangents = monotoneTangents(points)
  let d = `M ${fmt(points[0]![0])} ${fmt(points[0]![1])}`
  for (let i = 1; i < points.length; i++) {
    const [x0, y0] = points[i - 1]!
    const [x1, y1] = points[i]!
    const dx = (x1 - x0) / 3
    const c1 = [x0 + dx, y0 + (tangents[i - 1] ?? 0) * dx]
    const c2 = [x1 - dx, y1 - (tangents[i] ?? 0) * dx]
    d += ` C ${fmt(c1[0]!)} ${fmt(c1[1]!)}, ${fmt(c2[0]!)} ${fmt(c2[1]!)}, ${fmt(x1)} ${fmt(y1)}`
  }
  return d
}

/** Fritsch–Carlson tangents: the standard monotone-preserving cubic. */
function monotoneTangents(points: [number, number][]): number[] {
  const n = points.length
  const slopes: number[] = []
  for (let i = 0; i < n - 1; i++) {
    const [x0, y0] = points[i]!
    const [x1, y1] = points[i + 1]!
    const dx = x1 - x0
    slopes.push(dx === 0 ? 0 : (y1 - y0) / dx)
  }

  const tangents: number[] = Array.from({ length: n }, () => 0)
  tangents[0] = slopes[0] ?? 0
  tangents[n - 1] = slopes[n - 2] ?? 0
  for (let i = 1; i < n - 1; i++) {
    const s0 = slopes[i - 1] ?? 0
    const s1 = slopes[i] ?? 0
    tangents[i] = s0 * s1 <= 0 ? 0 : (s0 + s1) / 2
  }
  for (let i = 0; i < n - 1; i++) {
    const s = slopes[i] ?? 0
    if (s === 0) {
      tangents[i] = 0
      tangents[i + 1] = 0
      continue
    }
    const a = (tangents[i] ?? 0) / s
    const b = (tangents[i + 1] ?? 0) / s
    const h = Math.hypot(a, b)
    if (h > 3) {
      const t = 3 / h
      tangents[i] = t * a * s
      tangents[i + 1] = t * b * s
    }
  }
  return tangents
}

/** Close a line into a filled area against a baseline. */
export function areaPath(points: [number, number][], baseline: number, smooth = false): string {
  if (points.length === 0) return ''
  const line = linePath(points, smooth)
  const first = points[0]!
  const last = points[points.length - 1]!
  return `${line} L ${fmt(last[0])} ${fmt(baseline)} L ${fmt(first[0])} ${fmt(baseline)} Z`
}

/** Arc path for donuts and gauges, angles in radians clockwise from 12 o'clock. */
export function arcPath(
  cx: number,
  cy: number,
  outerRadius: number,
  innerRadius: number,
  startAngle: number,
  endAngle: number,
): string {
  const sweep = endAngle - startAngle
  // A full ring cannot be drawn as one arc — split it.
  if (sweep >= Math.PI * 2 - 1e-6) {
    const mid = startAngle + Math.PI
    return `${arcPath(cx, cy, outerRadius, innerRadius, startAngle, mid)} ${arcPath(cx, cy, outerRadius, innerRadius, mid, endAngle)}`
  }

  const p = (radius: number, angle: number): [number, number] => [
    cx + radius * Math.sin(angle),
    cy - radius * Math.cos(angle),
  ]
  const largeArc = sweep > Math.PI ? 1 : 0
  const [x0, y0] = p(outerRadius, startAngle)
  const [x1, y1] = p(outerRadius, endAngle)
  const [x2, y2] = p(innerRadius, endAngle)
  const [x3, y3] = p(innerRadius, startAngle)

  return [
    `M ${fmt(x0)} ${fmt(y0)}`,
    `A ${fmt(outerRadius)} ${fmt(outerRadius)} 0 ${largeArc} 1 ${fmt(x1)} ${fmt(y1)}`,
    `L ${fmt(x2)} ${fmt(y2)}`,
    `A ${fmt(innerRadius)} ${fmt(innerRadius)} 0 ${largeArc} 0 ${fmt(x3)} ${fmt(y3)}`,
    'Z',
  ].join(' ')
}

/** A rounded rectangle path with per-corner control, for bars that only round one end. */
export function barPath(
  x: number,
  y: number,
  width: number,
  height: number,
  radii: [number, number, number, number] = [0, 0, 0, 0],
): string {
  const w = Math.max(0, width)
  const h = Math.max(0, height)
  const limit = Math.min(w, h) / 2
  const [tl, tr, br, bl] = radii.map((r) => Math.max(0, Math.min(r, limit))) as [
    number,
    number,
    number,
    number,
  ]
  return [
    `M ${fmt(x + tl)} ${fmt(y)}`,
    `H ${fmt(x + w - tr)}`,
    tr ? `A ${fmt(tr)} ${fmt(tr)} 0 0 1 ${fmt(x + w)} ${fmt(y + tr)}` : '',
    `V ${fmt(y + h - br)}`,
    br ? `A ${fmt(br)} ${fmt(br)} 0 0 1 ${fmt(x + w - br)} ${fmt(y + h)}` : '',
    `H ${fmt(x + bl)}`,
    bl ? `A ${fmt(bl)} ${fmt(bl)} 0 0 1 ${fmt(x)} ${fmt(y + h - bl)}` : '',
    `V ${fmt(y + tl)}`,
    tl ? `A ${fmt(tl)} ${fmt(tl)} 0 0 1 ${fmt(x + tl)} ${fmt(y)}` : '',
    'Z',
  ]
    .filter(Boolean)
    .join(' ')
}

/** Trim float noise out of path data — smaller DOM, cleaner diffs in snapshots. */
const fmt = (n: number): string => {
  if (!Number.isFinite(n)) return '0'
  return String(Math.round(n * 100) / 100)
}
